/**
 * TanStack Query hook for the LLM-generated cross-bank overview narrative
 * (HANDOFF_DEAL_OVERVIEW_NARRATIVE_V1).
 *
 * Mirrors useTermSheetNarrative. Returns undefined while loading or on
 * 404 — DealIntelligenceCard treats undefined / 404 / is_fallback=true
 * as a signal to render its V2 deterministic copy on a per-field basis.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '../client';
import type { DealOverviewNarrativeResponse } from '../types';

export const dealOverviewNarrativeKeys = {
  all: ['dealOverviewNarrative'] as const,
  deal: (dealId: number) => [...dealOverviewNarrativeKeys.all, dealId] as const,
};

export function useDealOverviewNarrative(
  dealId: number | null | undefined,
  options?: { enabled?: boolean },
) {
  const { enabled = true } = options ?? {};
  return useQuery<DealOverviewNarrativeResponse>({
    queryKey: dealOverviewNarrativeKeys.deal(dealId ?? 0),
    queryFn: () => api.dealOverviewNarrative.get(dealId as number),
    enabled: enabled && !!dealId,
    // Cross-bank narrative regenerates on a 24h cycle; safe to cache for
    // longer than a per-sheet narrative.
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      // 404 = narrative not generated yet (deal has fewer than 2 ANALYZED
      // sheets, or Celery is still running). Don't hammer it; the next
      // route navigation will retry naturally.
      if (status === 404) {return false;}
      return failureCount < 2;
    },
  });
}
